import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import clsx from "clsx";
import {
  BarChart3, Users, Mail, Inbox, Gauge, ScrollText,
  ArrowLeft, LogOut, Shield, Menu, X, CreditCard, TestTube, Bot, Sparkles,
} from "lucide-react";

const NAV = [
  { to: "/admin",            icon: BarChart3,  label: "Overview",    end: true },
  { to: "/admin/users",      icon: Users,      label: "Users"              },
  { to: "/admin/plans",      icon: CreditCard, label: "Plans"              },
  { to: "/admin/mail",       icon: Mail,       label: "Mail Server"        },
  { to: "/admin/inbox",      icon: Inbox,      label: "Inbox"              },
  { to: "/admin/verticals",  icon: Bot,        label: "Verticals"          },
  { to: "/admin/prompts",    icon: Sparkles,   label: "AI Prompts"         },
  { to: "/admin/usage",      icon: Gauge,      label: "Usage"              },
  { to: "/admin/seed",       icon: TestTube,   label: "Seed Testing"       },
  { to: "/admin/audit-log",  icon: ScrollText, label: "Audit Log"          },
];

const MOBILE_NAV = [
  { to: "/admin",           icon: BarChart3,  label: "Overview", end: true },
  { to: "/admin/users",     icon: Users,      label: "Users"              },
  { to: "/admin/usage",     icon: Gauge,      label: "Usage"              },
  { to: "/admin/audit-log", icon: ScrollText, label: "Log"                },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="flex h-screen bg-paper overflow-hidden">
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        "fixed inset-y-0 left-0 z-50 w-60 bg-gray-950 flex flex-col transition-transform duration-300 lg:relative lg:translate-x-0 lg:flex-shrink-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-6 pb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center shadow-md shadow-red-500/30">
              <Shield size={15} className="text-white" />
            </div>
            <span className="text-white font-display font-bold text-lg tracking-tight">Admin</span>
          </div>
          <button onClick={() => setOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/[0.06]">
            <X size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 space-y-0.5">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) => clsx(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                isActive
                  ? "bg-red-500/15 text-red-400"
                  : "text-white/45 hover:text-white/75 hover:bg-white/[0.05]"
              )}>
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div className="px-3 pb-4 space-y-0.5">
          <div className="px-3 py-2 mb-1">
            <p className="text-[11px] text-white/30 font-semibold truncate">{user?.email}</p>
          </div>
          <button onClick={() => { navigate("/dashboard"); setOpen(false); }}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-white/40 hover:text-white/70 hover:bg-white/[0.05] text-sm font-medium transition-all">
            <ArrowLeft size={15} /> Back to App
          </button>
          <button onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-white/40 hover:text-white/70 hover:bg-white/[0.05] text-sm font-medium transition-all">
            <LogOut size={15} /> Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile topbar */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-gray-950 border-b border-white/[0.06] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-red-500 rounded-lg flex items-center justify-center">
              <Shield size={13} className="text-white" />
            </div>
            <span className="text-white font-display font-bold text-base">Admin</span>
          </div>
          <button onClick={() => setOpen(true)}
            className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/[0.08]">
            <Menu size={18} />
          </button>
        </div>

        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-gray-950/95 backdrop-blur-md border-t border-white/[0.08]">
          <div className="flex items-center justify-around px-2 py-2">
            {MOBILE_NAV.map((item) => {
              const isActive = item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);
              return (
                <NavLink key={item.to} to={item.to} end={item.end}
                  className="flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl">
                  <item.icon size={18} className={isActive ? "text-red-400" : "text-white/35"} />
                  <span className={clsx("text-[10px] font-semibold", isActive ? "text-red-400" : "text-white/25")}>
                    {item.label}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
